import { useEffect } from "react";
import { Button } from '@mui/material';

import { myUseState } from "~/hooks/myUseState";
import { useTrans } from "~/hooks/useTrans";
import { IUrlParams } from "~/interfaces";
import TariffWhatAbout from "~/components/TariffWhatAbout";
import BetterTariffRequestModal from "~/modals/BetterTariffRequestModal";
import RemoveBetterTariffModal from "~/modals/RemoveBetterTariffModal";
import { IAdminProject } from "~/providers/admin-interfaces";
import { IClientProject } from "~/providers/client-interfaces";
import { apiGetProjectAdvancedBillingConsent } from "~/providers/api";
import { formatDate } from "~/utils/utils";
import ButtonLink from "~/components/ButtonLink";
import { useLang } from "~/hooks/useLang";
import MyAlert from "~/mui/MyAlert";

// mam zakladni a nikdy jsem nemel zvyhodneny
// mam platny zvyhodneny
// mam zakladni a neplatny zvyhodneny
// mam VIP
type TVariant = "basic-no-better" | "basic-ok" | "basic-error" | "vip";

interface IState {
	showBetterTariffRequest: boolean;
	showRemoveBetterTarif: boolean;
	variant: TVariant;
	isDirty: boolean;
	expired: Date;
	approvedPlanError: string;
}

interface IBetterTariffOfferContent {
	params: IUrlParams;
	projectData: IClientProject | IAdminProject;
	onSave?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function BetterTariffOfferContent({
	params = null,
	projectData = null,
	onSave = () => {},
	onChange = () => {},
}: IBetterTariffOfferContent) {
	const { state, updateState } = myUseState<IState>({
		showBetterTariffRequest: false,
		showRemoveBetterTarif: false,
		variant: projectData.businessPlanType === "basic" ? "basic-no-better" : "vip",
		isDirty: false,
		expired: new Date(),
		approvedPlanError: "",
	});
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const isVisitor = projectData.userRole === "visitor";

	async function loadData() {
		if (projectData.businessPlanType === "basic" || projectData.businessPlanType === "advanced") {
			const advancedBillingConsentData = await apiGetProjectAdvancedBillingConsent("client", params.projectId, lang);

			if (advancedBillingConsentData.noErrors && advancedBillingConsentData.data.length > 0) {
				const firstItem = advancedBillingConsentData.data[0];
				const validTill = new Date(firstItem.validTill);
				const isValid = validTill.getTime() > Date.now();

				updateState({
					variant: isValid ? "basic-ok" : "basic-error",
					expired: validTill,
				});
			}
		}
	}

	function onBetterTarifSave(variant: TVariant) {
		updateState({
			showBetterTariffRequest: false,
			showRemoveBetterTarif: false,
			variant,
			isDirty: false,
		});
		onChange(false);
		loadData();
		onSave();
	}

	function updateDirty(isDirty: boolean) {
		updateState({ isDirty });
		onChange(isDirty);
	}

	function closeModal() {
		updateState({
			showBetterTariffRequest: false,
			showRemoveBetterTarif: false,
			isDirty: false,
		});
		onChange(false);
	}

	function getBetterProgramClick() {
		updateState({
			...projectData.approvedAdvancedPlan === false
				? {
					approvedPlanError: trans("adminDeniedAdvancedPlan"),
				}
				: {
					showBetterTariffRequest: true,
				},
		});
	}

	useEffect(() => {
		loadData();
	}, []);

	return <>
		{ state.variant === "basic-no-better" && <>
			<Button onClick={getBetterProgramClick} color="success" variant="contained" disabled={isVisitor}>
				{ trans("projectDashboardPage.getBetterProgram") }
			</Button>
			<TariffWhatAbout />
		</> }
		{ state.variant === "basic-ok" && <>
			<ButtonLink title={trans("betterTariffOfferContent.update")} onClick={() => updateState({ showBetterTariffRequest: true })} disabled={isVisitor} />
			<p className="margin-top-small">
				{ trans("betterTariffOfferContent.validUntil", { date: formatDate(state.expired, isCzech) }) }
			</p>
			<ButtonLink title={trans("betterTariffOfferContent.cancel")} onClick={() => updateState({ showRemoveBetterTarif: true })} disabled={isVisitor} />
		</> }
		{ state.variant === "basic-error" && <>
			<Button onClick={getBetterProgramClick} color="success" variant="contained" disabled={isVisitor}>
				{ trans("projectDashboardPage.getBetterProgram") }
			</Button>
			<p className="margin-top-small">
				{ trans("betterTariffOfferContent.expired", { date: formatDate(state.expired, isCzech) }) }
			</p>
		</> }
		{ state.variant === "vip" && <></> }
		{ state.showBetterTariffRequest && <BetterTariffRequestModal params={params} onChange={isDirty => updateDirty(isDirty)}
			onClose={closeModal} onSave={() => onBetterTarifSave("basic-ok")} /> }
		{ state.showRemoveBetterTarif && <RemoveBetterTariffModal params={params} onClose={closeModal} onSave={() => onBetterTarifSave("basic-error")} /> }
		{ state.approvedPlanError.length > 0 && <MyAlert title={trans("projectDashboardPage.getBetterProgram")} onClose={() => updateState({ approvedPlanError: "" })} cancelCaption={trans("close")}>
			{state.approvedPlanError}
		</MyAlert> }
	</>;
}
